import {
  ApplyInterstitialModal,
  Header,
  LayoutContainer,
  MiniButton,
  MiniLink,
  RteWrapper,
} from '@greystar/designsystem';
import {
  applyNow,
  closeModal,
  FeesAnalyticsDataProvider,
  GetLevelsResponse,
  goBackToComponent,
  Property,
  useAnalytics,
  useFormattedAddress,
  withBooleanFeatureFlag,
} from '@greystar/lib';
import { PropertyDetailsContactDisclaimersProps } from '@greystar/ui';
import {
  GetStaticComponentProps,
  RichText as JssRichText,
  Text as JssText,
  SitecoreContextValue,
} from '@sitecore-jss/sitecore-jss-nextjs';
import { useRouter } from 'next/router';
import { useState, JSX } from 'react';

interface ExtendedProps extends PropertyDetailsContactDisclaimersProps {
  sitecoreContext?: SitecoreContextValue & {
    property?: Property;
  };
}

const PropertyDetailsContactDisclaimers = (props: ExtendedProps): JSX.Element => {
  const analytics = useAnalytics();
  const property = props.sitecoreContext?.property;
  const property_location = useFormattedAddress(property?.location);
  const [isApplyModalOpen, setIsApplyModalOpen] = useState(false);
  const router = useRouter();

  const commonEventData = {
    action_type: '',
    state: property?.location?.state ?? '',
    property_location: property_location,
    property_id: property?.id ?? '',
    property_name: property?.name ?? '',
    module_name: 'floor plan',
    inpage_location: 'middle',
    click_text: '',
    click_type: 'internal',
    destination_url: '',
  };

  const handleCallClick_event = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    const eventData = {
      ...commonEventData,
      action_type: 'call click',
      click_text: property?.contact?.trackingPhoneNumber ?? property?.contact?.phoneNumber ?? '',
      destination_url: `tel:${
        property?.contact?.trackingPhoneNumber ?? property?.contact?.phoneNumber
      }`,
    };
    router.push(`tel:${property?.contact?.trackingPhoneNumber ?? property?.contact?.phoneNumber}`);
    analytics.trackEvent('property_call_click', eventData);
  };

  if (!property?.floorplans?.length || !property?.fees?.length) {
    return <></>;
  }

  const handleApply = () => {
    if (!property?.contact?.leaseOnlineUrl) {
      goBackToComponent(router, 'contact-us');
      analytics.trackEvent('property_apply_entry', {
        ...commonEventData,
        action_type: 'entry',
        click_text: 'Apply',
        destination_url: property?.contact?.leaseOnlineUrl ?? '',
      });
    } else {
      setIsApplyModalOpen(true);

      const eventData = {
        ...commonEventData,
        action_type: 'interstitial',
        click_text: 'Apply',
        destination_url: '',
      };

      analytics.trackEvent('property_apply_interstitial', eventData);
    }
  };

  function formatTime(time: string) {
    return time.replace('AM', 'am').replace('PM', 'pm');
  }

  return (
    <div className={'flex flex-col gap-8'}>
      {/* Contact */}
      <LayoutContainer flexDirection="column" className="lg:pl-[16px] pt-[22px] lg:pt-0">
        <Header as="h3" component="h5" className="mb-[10px]">
          <JssText field={props.data.overrideContactHeader ?? props.fields?.contactHeader} />
        </Header>
        <RteWrapper>
          <JssRichText field={props.data.overrideContactText ?? props.fields?.contactText} />
        </RteWrapper>
        <LayoutContainer flexDirection="row" gap="sm">
          <MiniButton
            label="Apply"
            variant="primary"
            icon={
              props.data.leaseOnlineLink
                ? {
                    position: 'right',
                    name: 'ExternalLink',
                  }
                : undefined
            }
            isDisabled={false}
            onClick={handleApply}
          />
          {props.data.contactNumber && (
            <MiniLink
              href={`tel:${props.data.contactNumber}`}
              label={props.data.contactNumber}
              variant="tertiary"
              icon={{ position: 'right', name: 'Phone' }}
              onClick={handleCallClick_event}
            />
          )}
        </LayoutContainer>
      </LayoutContainer>

      {/* Office hours */}
      {props.data.officeHours && (
        <LayoutContainer className={'flex flex-col lg:pl-[16px]'}>
          <Header as="h3" component="h5" className="mb-[10px]">
            <JssText
              field={{
                value: 'Office hours',
              }}
            />
          </Header>
          <RteWrapper>
            {Object.entries(props.data.officeHours).map(([key, value], i) => {
              const isLast = i === Object.entries(props.data.officeHours ?? {}).length - 1;
              const day = key.charAt(0).toUpperCase() + key.slice(1).slice(0, 2);
              const hours = (open: string, close: string) => {
                if (open === 'CLOSED') return 'Closed';
                return `${formatTime(open)} — ${formatTime(close)}`;
              };

              return (
                <div
                  key={key}
                  className={`text-[13px] py-2 ${
                    isLast ? 'pb-0' : 'border-b border-[#0a2245]/10'
                  } [&_p]:!text-[13px] [&_p]:!mb-0 [&_p]:inline-block`}
                >
                  <p className={'!text-[#767B83] w-10'}>
                    <JssText field={{ value: day }} />
                  </p>
                  <p>
                    <JssText field={{ value: hours(value.open, value.close) }} />
                  </p>
                </div>
              );
            })}
          </RteWrapper>
        </LayoutContainer>
      )}

      {/* Cost and Pricing */}
      <LayoutContainer flexDirection="column" className="lg:pl-[16px]">
        <Header as="h3" component="h5" className="mb-[10px]">
          <JssText field={props.data.overridefeesHeader ?? props.fields?.feesHeader} />
        </Header>
        <RteWrapper>
          <JssRichText field={props.data.overridefeesDisclaimer ?? props.fields?.feesDisclaimer} />
        </RteWrapper>
      </LayoutContainer>

      {/* Floorplans */}
      <LayoutContainer flexDirection="column" className="lg:pl-[16px]">
        <Header as="h3" component="h5" className="mb-[10px]">
          <JssText field={props.data.overridefloorplansHeader ?? props.fields?.floorplansHeader} />
        </Header>
        <RteWrapper>
          <JssRichText
            field={props.data.overridefloorplansDisclaimer ?? props.fields?.floorplansDisclaimer}
          />
        </RteWrapper>
      </LayoutContainer>
      {isApplyModalOpen && (
        <ApplyInterstitialModal
          isOpen={isApplyModalOpen}
          onClose={() => {
            closeModal({
              setIsModalOpen: setIsApplyModalOpen,
              trackEvent: analytics.trackEvent,
              eventData: {
                ...commonEventData,
              },
              applyNowUrl: property?.contact?.leaseOnlineUrl,
              eventName: 'property_apply_interstitial_close',
            });
          }}
          onAccept={() => {
            applyNow({
              router,
              setIsModalOpen: setIsApplyModalOpen,
              applyNowUrl: property?.contact?.leaseOnlineUrl,
              trackEvent: analytics.trackEvent,
              eventName: 'property_apply_entry',
              eventData: {
                ...commonEventData,
                action_type: 'entry',
                click_text: 'Continue',
                destination_url: property?.contact?.leaseOnlineUrl ?? '',
              },
            });
          }}
        />
      )}
    </div>
  );
};

export const getStaticProps: GetStaticComponentProps = async (
  rendering,
  layoutData
): Promise<ExtendedProps> => {
  const props = rendering as unknown as ExtendedProps;
  const property = layoutData.sitecore.context.property as Property;

  const response: ExtendedProps = {
    ...props,
    sitecoreContext: layoutData.sitecore.context,
    data: {
      officeHours: null,
      notFound: true,
      contactNumber: null,
      leaseOnlineLink: null,
      overrideContactHeader: null,
      overrideContactText: null,
      overridefeesHeader: null,
      overridefeesDisclaimer: null,
      overridefloorplansHeader: null,
      overridefloorplansDisclaimer: null,
    },
  };

  if (!property) {
    return response;
  }

  const locationsData = layoutData.sitecore.context.locationsData as GetLevelsResponse;

  if (locationsData?.search?.results?.length && property?.location?.state) {
    const stateData = locationsData.search.results.find(
      (result) =>
        result.name && result.name.toLowerCase() === property.location?.state?.toLowerCase()
    );

    if (stateData) {
      return {
        ...response,
        data: {
          ...response.data,
          notFound: false,
          officeHours: property.officeHours ?? null,
          contactNumber:
            property?.contact?.trackingPhoneNumber ?? property?.contact?.phoneNumber ?? null,
          leaseOnlineLink: property.contact?.leaseOnlineUrl ?? null,
          overrideContactHeader:
            stateData.contactHeader && stateData.contactHeader.value !== ''
              ? stateData.contactHeader
              : null,
          overrideContactText:
            stateData.contactText && stateData.contactText.value !== ''
              ? stateData.contactText
              : null,
          overridefeesHeader:
            stateData.feesHeader && stateData.feesHeader.value !== '' ? stateData.feesHeader : null,
          overridefeesDisclaimer:
            stateData.feesDisclaimer && stateData.feesDisclaimer.value !== ''
              ? stateData.feesDisclaimer
              : null,
          overridefloorplansHeader:
            stateData.floorplansHeader && stateData.floorplansHeader.value !== ''
              ? stateData.floorplansHeader
              : null,
          overridefloorplansDisclaimer:
            stateData.floorplansDisclaimer && stateData.floorplansDisclaimer.value !== ''
              ? stateData.floorplansDisclaimer
              : null,
        },
      };
    }
  }

  return {
    ...response,
    data: {
      ...response.data,
      officeHours: property.officeHours ?? null,
      notFound: false,
      contactNumber:
        property?.contact?.trackingPhoneNumber ?? property?.contact?.phoneNumber ?? null,
      leaseOnlineLink: property.contact?.leaseOnlineUrl ?? null,
    },
  };
};

const ContactDisclaimerWrapper = (props: ExtendedProps): JSX.Element => {
  return (
    <FeesAnalyticsDataProvider
      initialData={{ module_name: 'property details contact disclaimers' }}
    >
      <PropertyDetailsContactDisclaimers {...props} />
    </FeesAnalyticsDataProvider>
  );
};

export default withBooleanFeatureFlag<ExtendedProps>(ContactDisclaimerWrapper);
